import React, { useEffect, useMemo, useState } from "react";
import SafeIcon from "../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import { useClient } from "../../context/ClientContext";
import { clientBillService, amountByCurrency, vendorName } from "../../services/client/clientBillService";
import { normalizeAccountType } from "../../services/coaService";
import ClientBillsFilters from "../../components/clients/bills/ClientBillsFilters";
import ClientBillsTable from "../../components/clients/bills/ClientBillsTable";
import ClientExpensesFilters from "../../components/clients/bills/ClientExpensesFilters";
import ClientExpensesTable from "../../components/clients/bills/ClientExpensesTable";
import ClientBillDetailsModal from "../../components/clients/modals/ClientBillDetailsModal";
import ClientExpenseDetailsModal from "../../components/clients/modals/ClientExpenseDetailsModal";

const { FiAlertTriangle, FiCheckCircle, FiClock, FiDollarSign, FiFileText, FiRefreshCw, FiTrendingDown } = FiIcons;
const PAGE_SIZE = 15;
const today = () => new Date().toISOString().slice(0, 10);

function formatMoney(value, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
  }
}

function groupLabel(totals, fallbackCurrency) {
  const entries = Object.entries(totals);
  if (!entries.length) return formatMoney(0, fallbackCurrency);
  if (entries.length === 1) return formatMoney(entries[0][1], entries[0][0]);
  return entries.map(([currency, value]) => `${currency} ${value.toFixed(2)}`).join(" · ");
}

function SummaryCard({ label, value, icon, tone, note }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 truncate text-2xl font-black tracking-tight text-slate-900">{value}</p>
          {note && <p className="mt-1 text-xs text-slate-400">{note}</p>}
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><SafeIcon icon={icon} className="text-lg" /></span>
      </div>
    </section>
  );
}

function inDateRange(value, startDate, endDate) {
  const date = String(value || "").slice(0, 10);
  return (!startDate || date >= startDate) && (!endDate || date <= endDate);
}

export default function ClientBills() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id || null;
  const organizationName = activeWorkspace?.name || "Your business";
  const currency = String(activeWorkspace?.currency || "USD").toUpperCase();

  const [bills, setBills] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadedOrganizationId, setLoadedOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bills");
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [billPage, setBillPage] = useState(1);
  const [expensePage, setExpensePage] = useState(1);
  const [billFilters, setBillFilters] = useState({ search: "", status: "all", startDate: "", endDate: "", sortBy: "newest" });
  const [expenseFilters, setExpenseFilters] = useState({ search: "", categoryId: "all", vendor: "all", startDate: "", endDate: "", sortBy: "newest" });

  const loadData = async (orgId, initial = false) => {
    if (!orgId) return;
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const [billData, expenseData] = await Promise.all([
        clientBillService.getClientBills({ organizationId: orgId }),
        clientBillService.getClientExpenses({ organizationId: orgId }),
      ]);
      if (orgId !== organizationId) return;
      setBills(billData.bills);
      setExpenses(expenseData);
      setLoadedOrganizationId(orgId);
    } catch (loadError) {
      if (orgId === organizationId) setError(loadError?.message || "Unable to load financial records.");
    } finally {
      if (orgId === organizationId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    setBills([]);
    setExpenses([]);
    setLoadedOrganizationId(null);
    setSelectedBill(null);
    setSelectedExpense(null);
    setBillPage(1);
    setExpensePage(1);
    setBillFilters({ search: "", status: "all", startDate: "", endDate: "", sortBy: "newest" });
    setExpenseFilters({ search: "", categoryId: "all", vendor: "all", startDate: "", endDate: "", sortBy: "newest" });
    if (!organizationId) {
      setLoading(false);
      setError("Select a business to view its bills and expenses.");
      return;
    }
    setError("");
    loadData(organizationId, true);
  }, [organizationId]);

  const showingData = loadedOrganizationId === organizationId;
  const currentBills = showingData ? bills : [];
  const currentExpenses = showingData ? expenses : [];

  const visibleBills = useMemo(() => {
    const query = billFilters.search.trim().toLocaleLowerCase();
    return currentBills
      .filter((bill) => {
        const status = String(bill.displayStatus || bill.status || "").toLowerCase();
        return (billFilters.status === "all" || status === billFilters.status) &&
          inDateRange(bill.bill_date, billFilters.startDate, billFilters.endDate) &&
          (!query || `${bill.bill_number || ""} ${vendorName(bill.vendor)}`.toLocaleLowerCase().includes(query));
      })
      .sort((a, b) => {
        if (billFilters.sortBy === "due") return String(a.due_date || "").localeCompare(String(b.due_date || ""));
        if (billFilters.sortBy === "amount") return (Number(b.total) || 0) - (Number(a.total) || 0);
        return String(b.bill_date || "").localeCompare(String(a.bill_date || ""));
      });
  }, [currentBills, billFilters]);

  const visibleExpenses = useMemo(() => {
    const query = expenseFilters.search.trim().toLocaleLowerCase();
    return currentExpenses
      .filter((expense) => {
        const payee = expense.vendorName || expense.payee || "";
        return (expenseFilters.categoryId === "all" || expense.account_id === expenseFilters.categoryId) &&
          (expenseFilters.vendor === "all" || payee === expenseFilters.vendor) &&
          inDateRange(expense.date, expenseFilters.startDate, expenseFilters.endDate) &&
          (!query || `${expense.description || ""} ${payee} ${expense.account?.name || ""}`.toLocaleLowerCase().includes(query));
      })
      .sort((a, b) => {
        if (expenseFilters.sortBy === "oldest") return String(a.date || "").localeCompare(String(b.date || ""));
        if (expenseFilters.sortBy === "amount") return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        return String(b.date || "").localeCompare(String(a.date || ""));
      });
  }, [currentExpenses, expenseFilters]);

  useEffect(() => { setBillPage(1); }, [billFilters, organizationId]);
  useEffect(() => { setExpensePage(1); }, [expenseFilters, organizationId]);

  const pagedBills = visibleBills.slice((billPage - 1) * PAGE_SIZE, billPage * PAGE_SIZE);
  const pagedExpenses = visibleExpenses.slice((expensePage - 1) * PAGE_SIZE, expensePage * PAGE_SIZE);

  const periodBills = currentBills.filter((bill) => inDateRange(bill.bill_date, billFilters.startDate, billFilters.endDate));
  const periodExpenses = currentExpenses.filter((expense) => inDateRange(expense.date, expenseFilters.startDate, expenseFilters.endDate));
  const notVoidedBills = periodBills.filter((bill) => !["void", "voided", "cancelled", "canceled"].includes(String(bill.status || "").toLowerCase()));
  const outstandingBills = notVoidedBills.filter((bill) => !["draft", "paid"].includes(String(bill.status || "").toLowerCase()) && Number(bill.balance_due) > 0);
  const overdueBills = outstandingBills.filter((bill) => bill.due_date && String(bill.due_date).slice(0, 10) < today());
  const expensesByCurrency = amountByCurrency(periodExpenses, (expense) => expense.debit);
  const outstandingByCurrency = amountByCurrency(outstandingBills, (bill) => bill.balance_due);
  const overdueByCurrency = amountByCurrency(overdueBills, (bill) => bill.balance_due);
  const paidBillsCount = notVoidedBills.filter((bill) => String(bill.status || "").toLowerCase() === "paid").length;

  const categories = [...new Map(currentExpenses.filter((row) => row.account?.id).map((row) => [row.account.id, row.account])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const vendors = [...new Set(currentExpenses.map((row) => row.vendorName || row.payee).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const refresh = () => {
    if (organizationId && !refreshing) loadData(organizationId);
  };
  const updateBillFilter = (name, value) => setBillFilters((state) => ({ ...state, [name]: value }));
  const updateExpenseFilter = (name, value) => setExpenseFilters((state) => ({ ...state, [name]: value }));

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">Financial management</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Bills &amp; Expenses</h1>
          <p className="mt-2 text-sm text-slate-600">Track business expenses, vendor bills, and payment obligations.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Selected business</p>
            <p className="mt-1 max-w-xs truncate text-sm font-bold text-slate-800">{organizationName}</p>
          </div>
          <button type="button" onClick={refresh} disabled={!organizationId || loading || refreshing} className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50">
            <SafeIcon icon={FiRefreshCw} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"><div className="flex items-start gap-2"><SafeIcon icon={FiAlertTriangle} className="mt-0.5" /><div><p className="font-semibold">Bills and expenses could not be loaded</p><p className="mt-1">{error}</p></div></div></div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Total Expenses" value={showingData ? groupLabel(expensesByCurrency, currency) : "—"} icon={FiTrendingDown} tone="bg-violet-50 text-violet-700" note="Posted expense-category debits in the selected period" />
        <SummaryCard label="Outstanding Bills" value={showingData ? groupLabel(outstandingByCurrency, currency) : "—"} icon={FiDollarSign} tone="bg-amber-50 text-amber-700" note={`${outstandingBills.length} open bills in the selected period`} />
        <SummaryCard label="Overdue Bills" value={showingData ? groupLabel(overdueByCurrency, currency) : "—"} icon={FiClock} tone="bg-rose-50 text-rose-700" note={`${overdueBills.length} past-due bills`} />
        <SummaryCard label="Bills Paid" value={showingData ? paidBillsCount.toLocaleString() : "—"} icon={FiCheckCircle} tone="bg-emerald-50 text-emerald-700" note="Paid bills in the selected period" />
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[["bills", "Bills", FiFileText], ["expenses", "Expenses", FiTrendingDown]].map(([value, label, icon]) => (
          <button key={value} type="button" onClick={() => setTab(value)} className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition ${tab === value ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <SafeIcon icon={icon} /> {label}
          </button>
        ))}
      </div>

      {tab === "bills" ? (
        <>
          <ClientBillsFilters {...billFilters} onChange={updateBillFilter} onReset={() => setBillFilters({ search: "", status: "all", startDate: "", endDate: "", sortBy: "newest" })} />
          <ClientBillsTable bills={pagedBills} loading={loading} page={billPage} pageSize={PAGE_SIZE} totalCount={visibleBills.length} onPageChange={setBillPage} onView={setSelectedBill} />
          {!loading && showingData && bills.length === 0 && !error && <p className="text-center text-xs text-slate-500">No bills have been recorded for this business.</p>}
        </>
      ) : (
        <>
          <ClientExpensesFilters {...expenseFilters} categories={categories} vendors={vendors} onChange={updateExpenseFilter} onReset={() => setExpenseFilters({ search: "", categoryId: "all", vendor: "all", startDate: "", endDate: "", sortBy: "newest" })} />
          <ClientExpensesTable expenses={pagedExpenses} loading={loading} page={expensePage} pageSize={PAGE_SIZE} totalCount={visibleExpenses.length} onPageChange={setExpensePage} onView={setSelectedExpense} />
          {!loading && showingData && expenses.length === 0 && !error && <p className="text-center text-xs text-slate-500">No posted expense transactions have been recorded for this business.</p>}
        </>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <SafeIcon icon={FiFileText} />
        Expenses are shown from ledger lines classified by the organization’s chart of accounts; bill records are listed separately to avoid double counting.
      </div>

      <ClientBillDetailsModal organizationId={organizationId} bill={selectedBill} onClose={() => setSelectedBill(null)} />
      <ClientExpenseDetailsModal organizationId={organizationId} expense={selectedExpense} onClose={() => setSelectedExpense(null)} />
    </div>
  );
}